import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { api } from "@/api";
import { Office } from "@/api/officies/officies.ts";
import { FaBookmark } from "react-icons/fa";
import { S3_URL } from "@/api/s3/s3";
import { Button } from "@heroui/react";

// Расширенный интерфейс для офиса с дополнительными полями
interface ExtendedOffice extends Office {
  main_photo?: string;
  likeCount?: number;
  viewCount?: number;
}

export function LikedOfficesPage() {
  const [likedOffices, setLikedOffices] = useState<ExtendedOffice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLikedOffices();
  }, []);

  const loadLikedOffices = async () => {
    try {
      setLoading(true);
      const response = await api.officies.likes();
      setLikedOffices(response.data);
    } catch (err) {
      console.error(err);
      toast.error("Произошла ошибка при загрузке понравившихся офисов!");
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (office: ExtendedOffice) => {
    trackEvent("office_card_click", {
      office_id: office.id,
      office_name: office.display_name,
      office_price: office.price,
      office_area: office.area,
    });
    window.open(
      `https://elasticspace.app/prop/${office.id}-${office.display_name}`,
      "_blank",
    );
  };

  const trackEvent = (eventName: string, data: Record<string, unknown>) => {
    // Здесь можно добавить аналитику
    console.log(eventName, data);
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <span className="font-medium">Понравившиеся офисы</span>
          </div>
          <div className="flex items-center justify-center h-64">
            <div className="text-foreground-500">Загрузка...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
        <div className="flex flex-row justify-between w-full items-center mb-4">
          <span className="font-medium">Понравившиеся офисы</span>
        </div>

        {likedOffices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <FaBookmark className="text-4xl text-foreground-300" />
            <div className="text-center">
              <h3 className="text-lg font-medium text-foreground-700 mb-2">
                У вас пока нет понравившихся офисов
              </h3>
              <p className="text-foreground-500">
                Перейдите в маркетплейс, чтобы найти и добавить в избранное
                интересные офисы
              </p>
            </div>
            <Button
              onPress={() => window.open("https://elasticspace.app", "_blank")}
            >
              Перейти в маркетплейс
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {likedOffices.map((office) => (
              <div
                key={`${office.location}_${office.id}`}
                className="flex flex-col rounded-xl relative bg-white dark:bg-zinc-800 border-2 border-foreground-200 dark:border-foreground-100 cursor-pointer"
                onClick={() => handleCardClick(office)}
              >
                <div className="relative">
                  <img
                    alt={office.display_name}
                    className="w-full h-40 object-cover rounded-t-xl"
                    src={
                      office.main_photo
                        ? `${S3_URL}/${office.main_photo}`
                        : "/placeholder-office.svg"
                    }
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/placeholder-office.svg";
                    }}
                  />
                  <div className="absolute top-2 right-2 bg-white dark:bg-zinc-800 rounded-full p-1">
                    <FaBookmark className="text-success text-sm" />
                  </div>
                </div>
                <div className="p-3 flex flex-col gap-2">
                  <div className="flex flex-row items-center justify-between w-full mt-auto">
                    <h2 className="font-semibold text-sm line-clamp-2">
                      {office.display_name}
                    </h2>
                    <div className="text-right">
                      <div className="text-sm font-bold">
                        ₽{office.price.toLocaleString("ru-RU")}
                      </div>
                      <div className="text-xs text-foreground-500">/мес.</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
